export interface ISelectionHandler {
    refresh: () => void;
    onUpPress: () => void;
    onDownPress: () => void;
    getSelectedItemText: () => string;
    getActiveElement: () => HTMLAnchorElement;
    onTabChange: () => void;
    resetSelection: () => void;
}

const SEARCH_RESULTS_SELECTOR = '#search-results .section-content';

export class SelectionHandler implements ISelectionHandler {
    searchResults: HTMLAnchorElement[] = [];

    /**
     * Index of the currently selected item in the search results.
     * -1 means no item is selected.
     * 0 means the first item is selected.
     * 1 means the second item is selected.
     * and so on...
     */
    selected = -1;

    refresh() {
        this.searchResults = this.getSearchResults();
        this.setSelectedItem();
    }

    resetSelection() {
        this.removeSelectionAttribute();
        this.selected = -1;
    }

    onTabChange() {
        this.refresh();
        this.resetSelection();
    }

    onUpPress() {
        this.removeSelectionAttribute();

        if (this.searchResults.length < 1) {
            return;
        }

        if (this.selected > 0) {
            this.selected--;
        } else {
            this.selected = this.searchResults.length - 1;
        }

        this.setSelectedItem();
    }

    onDownPress() {
        this.removeSelectionAttribute();

        if (this.searchResults.length < 1) {
            return;
        }

        if (this.selected < this.searchResults.length - 1) {
            this.selected++;
        } else {
            this.selected = 0;
        }

        this.setSelectedItem();
    }

    setSelectedItem() {
        if (this.selected < 0) {
            return;
        }

        const activeElement = this.getActiveElement();

        // Selection can't be made at the same index where it was before, so we reset it
        if (!activeElement) {
            this.resetSelection();
            return;
        }

        // setting data-selected attribute
        activeElement.dataset.selected = 'true';

        // scrolling to item if not in view
        // TODO: remove this and mock `scrollIntoView` properly.
        // It doesn't exist in node environment when testing with Jest.
        if (typeof activeElement.scrollIntoView !== 'function') {
            return;
        }

        activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
        });
    }

    getSelectedItemText() {
        const activeElement = this.getActiveElement();
        return activeElement?.innerText.trim() || '';
    }

    removeSelectionAttribute() {
        const activeElement = this.getActiveElement();

        if (!activeElement?.dataset.selected) {
            return;
        }

        delete activeElement.dataset.selected;
    }

    getSearchResults() {
        return [...document.querySelectorAll(SEARCH_RESULTS_SELECTOR)] as HTMLAnchorElement[];
    }

    getActiveElement(): HTMLAnchorElement {
        return this.searchResults[this.selected];
    }
}
